import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useState,
} from 'react'
import { useQuery, UseQueryResult } from 'react-query'
import { Loader } from '@crossfit/ui'

import { fetchPage } from 'services/content'
import ErrorPage from 'pages/Error'

export interface Login {
  buttonText: string
  emailLabelText: string
  passwordLabelText: string
  subTitleText: string
  titleText: string
}

export type BannerMessageCTA = {
  text: string
  url: string
}

export type BannerMessage = {
  bannerType: 'info' | 'error' | 'pending' | 'warning'
  text: string
  cta: BannerMessageCTA
}

type Settings = {
  programming: {
    ineligible: Ineligible
    programmingContentReleaseDay: string
    programmingContentReleaseTime: string
    programmingStartDate: string
    surveyRequiredForNewCoaches: string
    latestProgrammingWeekDate: string
    strings: Strings
    waitlist: Waitlist
    waitlistSuccess: WaitlistSuccess
    welcome: Welcome
    welcomeSuccess: WelcomeSuccess
  }
  starterKit: {
    starterKitBannerExpiryButtonText: string
    starterKitBannerExpiryButtonUrl: string
    starterKitBannerExpiryText: string
  }
  bannerMessages: {
    askNotApplied: BannerMessage
    askReviewApplication: BannerMessage
    starterKitExpiry: BannerMessage
    askAppliedAccessExpiring: BannerMessage
  }
  affiliateStanding: {
    lockedOutBanners: {
      credential: {
        cta: {
          text: string
          url: string
        }
        description: string
        headlineText: string
      }
      license: {
        description: string
        headlineText: string
      }
      licenseCopy: {
        cta: {
          text: string
          url: string
        }
        description: string
        headlineText: string
      }
      payment: {
        headlineText: string
        description: string
        cta: {
          text: string
          url: string
        }
      }
    }
    lockedOutPage: {
      lor: {
        description: string
        headlineText: string
      }
      managerCoach: {
        description: string
        headlineText: string
      }
    }
  }
}

export interface Ineligible {
  descriptionText: string
  titleText: string
}

type Strings = {
  aboutTheWorkoutText: string
  accessoryText: string
  alternateWorkoutText: string
  atAGlanceText: string
  bodyweightText: string
  breakTimeText: string
  classPlanText: string
  coachingGoalsText: string
  dailyFocusText: string
  dailyTipText: string
  equipmentText: string
  generalWarmUpText: string
  intendedStimulusText: string
  logisticsText: string
  mainLiftText: string
  modificationsText: string
  movementFocusText: string
  movementSubstitutionsText: string
  movementText: string
  notesText: string
  otherConsiderationsText: string
  overviewText: string
  progressionStepText: string
  returnToWeekViewText: string
  scalingText: string
  scoreText: string
  specificWarmUpText: string
  stimulusGoalsText: string
  strategyText: string
  stretchingText: string
  substitutionText: string
  videoOverviewText: string
  videoResourcesText: string
  warmUpText: string
  whiteboardText: string
  workoutOrderText: string
  workoutText: string
}

export interface Waitlist {
  buttonText: string
  currentProgrammingLabelText: string
  descriptionText: string
  inPersonLabelText: string
  numberOfMembersLabelText: string
  subTitleText: string
  titleText: string
  workoutSoftwareLabelText: string
}

export interface WaitlistSuccess {
  buttonText: string
  descriptionText: string
  subTitleText: string
  titleText: string
}

export interface Welcome {
  descriptionText: string
  titleText: string
  viewClassPlansText: string
}

export interface WelcomeSuccess {
  agreeToWaiverLabelText: string
  buttonText: string
  currentProgrammingLabelText: string
  descriptionText: string
  titleText: string
}

type Context = UseQueryResult<Settings> & {
  programmingActivationDate: Date | undefined
  setProgrammingActivationDate: Dispatch<SetStateAction<Date>>
}

export const SettingsContext = createContext({} as Context)

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const [programmingActivationDate, setProgrammingActivationDate] =
    useState<Date>()
  const query = useQuery(
    'settings',
    () => fetchPage<Settings>(`/affiliate-toolkit-settings`),
    {
      retry: 3,
      retryDelay: 1000,
    },
  )

  if (query.isLoading) {
    return <Loader />
  }

  if (query.error) {
    return <ErrorPage />
  }

  return (
    <SettingsContext.Provider
      value={{
        ...query,
        programmingActivationDate,
        setProgrammingActivationDate,
      }}
    >
      {children}
    </SettingsContext.Provider>
  )
}

export function useSettings<T>(callback: (settings) => T) {
  const settings = useContext(SettingsContext)
  return callback(settings)
}

export function useWelcomeSuccessText() {
  const settings = useContext(SettingsContext)

  return settings.data.programming.welcomeSuccess
}

export function useStrings() {
  const settings = useContext(SettingsContext)

  return settings.data.programming.strings
}

export function useProgrammingDates() {
  const { programmingActivationDate, setProgrammingActivationDate, data } =
    useContext(SettingsContext)

  return {
    setProgrammingActivationDate,
    programmingActivationDate,
    latestProgrammingWeekDate: data.programming.latestProgrammingWeekDate,
    programmingStartDate: data.programming.programmingStartDate,
  }
}
